<template>
  <v-card-text
    v-if="tender && tender.tenderId"
    id="tender-tabs"
    class="px-0 pt-5 pb-0">
    <v-tabs
      ref="tabs"
      v-model="tab"
      background-color="var(--color-main-green)"
      class="tender-tabs tabs mb-5 "
      color="var(--black-color)"
      show-arrows>
      <v-tab
        v-for="(item, index) in tabs"
        :key="index"
        class="tab h7-text-medium px-3'"
        :data-qa="`tab-${item.value}`">
        <BadgeAI
          v-if="item.value == 'aiRequirements'"
          :offset-x="1"
          :offset-y="6"
          :visible="true">
          {{ item.name }}
        </BadgeAI>
        <template v-else>
          {{ item.name }}
        </template>
        <div
          class="tab__badge h9-text-bold black--text'"
          :data-qa="`tab-${item.value}-badge`">
          <sIcon
            v-if="load_members && item.value == 'members'"
            class="mb-4"
            color="var(--secondary__green_not_active)"
            size="8"
            spin
            type="loading"/>
          <span v-else-if="item.count"> {{ item.count }} </span>
        </div>
      </v-tab>
      <div class="spacer"/>
      <template v-if="tabs[tab] && tabs[tab].value == 'lots'"/>
      <template v-else-if="tabs[tab] && tabs[tab].value == 'offers'">
        <SearchInput
          class="mr-2"
          debounce
          :search.sync="offersSearch"
          small
          @input="applyOfferSearch"/>
        <TooltipedBtn
          v-if="isHistoryButtonEnabled"
          data-qa="btn-history-of-all-proposals"
          icon="graph"
          :small-btn="true"
          :text="$t('Tenders.HistoryOfAllProposals')"
          :texted="$vuetify.breakpoint.lgAndUp"
          @click="$router.push({ name: 'bidsHistory', params: { tenderId: tender.tenderId, org: $route.params.org} })"/>
        <DownloadDocumentArchive
          v-if="tender.bids && tender.bids.length > 0"
          data-qa="btn-download-all-file"
          :disabled="disabledDocumentArchive"
          :hint="disabledDocumentArchive ? $t('Tenders.ArchiveNotAvailableAtStatus') : ''"
          :label="$t('Common.downloadArchive')"
          small-btn
          :supplier-id="0"
          :tender-id="tender.tenderId"
          :title="`${$t('Common.biddersDocumentations', { tenderNumber: tender.tenderNumber })}`"/>
      </template>
      <template v-else-if="tabs[tab] && tabs[tab].value == 'documents'">
        <ImportDialog
          v-if="can_add_file && can_edit"
          :apply="addNewFiles"
          :create-file-handler="createFileHandler"
          :files.sync="new_fles"
          files-types
          subtitle=" "
          :title="$t('Tenders.AddNewDoc')">
          <template #activator="{ open }">
            <div
              class="flex"
              style="height: 100%;">
              <TooltipedBtn
                class="my-auto mr-3"
                data-qa="btn-add-new-file"
                icon="add-file"
                small-btn
                :text="bigFirstLetter($t('Tenders.AddNew'))"
                :texted="$vuetify.breakpoint.lgAndUp"
                @click="open"/>
            </div>
          </template>
        </ImportDialog>
        <DownloadDocumentArchive
          v-if="files.length > 0"
          data-qa="btn-download-all-file"
          :label="$t('Common.downloadArchive')"
          small-btn
          :tender-id="tender.tenderId"
          :title="`${$t('Common.organizerDocumentations', { tenderNumber: tender.tenderNumber })}`"/>
      </template>
      <template v-else-if="tabs[tab] && tabs[tab].value == 'members'">
        <MembersBtns
          :bidding_type="bidding_type"
          :can_invite="can_invite"
          :member-search.sync="memberSearch"
          :members.sync="members"
          :tender="tender"
          @setMembersGroups="setMembersGroups"
          @updateSubscribed="getSubscribedToCategory"/>
      </template>
      <template v-else-if="tabs[tab] && tabs[tab].value == 'aiRequirements'">
        <AiRequirementsAddedEdit
          :open.sync="requirement.editModal"
          :tender="tender">
          <TooltipedBtn
            slot="activator"
            class="ml-4"
            data-qa="btn-add-ai-requirement"
            disabled
            icon="plus-circle"
            :small-btn="true"
            :text="$t('AIRequirements.AddAIRequirement')"
            :texted="$vuetify.breakpoint.lgAndUp"
            @click="requirement.editModal = true"/>
        </AiRequirementsAddedEdit>
      </template>
    </v-tabs>
    <v-tabs-items
      id="content-container"
      v-model="tab"
      class="main__green">
      <!-- lots -->
      <LotsTab
        :in_creating="in_creating"
        :return-to-step="returnToStep"
        :tab="tabs[tab]"
        :tender="tender"/>
      <!-- offers -->
      <v-tab-item
        v-if="tabs.some(tab => tab.value == 'offers')"
        class="offers"
        data-qa="item-tab-offers">
        <OffersTab
          ref="offers_tab"
          :change_winner_mode.sync="change_winner_mode"
          :contranct-props="contranctProps"
          :critery-card="criteryCard"
          infinity_selector="#tender-tabs"
          :load-critery-card="load_criteryCard"
          :tab="tabs[tab]"
          :tender="tender"/>
      </v-tab-item>
      <!-- criteria -->
      <v-tab-item
        v-if="$can('use', 'useCriteriaCards')"
        :class="tabs[tab] ? tabs[tab].value : ''"
        data-qa="item-tab-criteria">
        <EmptyBlock
          v-if="!tender.criteriaCard"
          custom-class="mt-0 pa-10"
          data-qa="criteria-noData"
          :text="$t('Tenders.NonPriceCriteriaNotUsed')">
          <template #append>
            <sButton
              v-if="in_creating"
              class="mt-5 block"
              @click="() => returnToStep('NotPriceCriteria')">
              {{ $t('Tenders.ToAddCriterion') }}
            </sButton>
          </template>
        </EmptyBlock>
        <template v-else>
          <v-skeleton-loader
            v-if="load_criteryCard"
            :boilerplate="false"
            class="mt-4"
            :tile="false"
            type=" table-thead, table-tbody, table-tbody"
            width="100%"/>
          <template v-else-if="criteryCard">
            <v-card
              v-if="criteryCard.title || criteryCard.useReducedPrice"
              class="main__light_green mb-5 rounded-lg">
              <template v-if=" criteryCard.title">
                <v-card-subtitle class="h6-text black--text text-start pb-0">
                  {{ $t('Tenders.YouUse') }}
                </v-card-subtitle>
                <v-card-title class="h3-headline black--text pt-0">
                  {{ criteryCard.title }}
                </v-card-title>
              </template>
              <div
                v-if="criteryCard.useReducedPrice"
                class="px-4 pb-5">
                <div class="h9-label">
                  {{ $t('Tenders.GivenPrice') }}
                </div>
                <ReducedPriceFormula :card="criteryCard"/>
              </div>
              <CriteriaCardVars
                v-else
                :card="criteryCard"
                class="px-4 pb-5"/>
            </v-card>
            <CriteriaTable
              :bidding_type="bidding_type"
              :card="criteryCard"
              :items="criteryCard.criterias"
              td-color="main__light_green"/>
          </template>
        </template>
      </v-tab-item>
      <!-- documents -->
      <v-tab-item
        :class="tabs[tab] ? tabs[tab].value : ''"
        data-qa="item-tab-documents">
        <EmptyBlock
          v-if="!files.length"
          custom-class="mt-0 pa-10 no-docs"
          data-qa="document-noData"
          :text="$t('Tenders.NoData')">
          <template #append>
            <sButton
              v-if="in_creating"
              class="block mt-5"
              color="var(--color-error)"
              @click="returnToStep('Documents')">
              {{ $t('Tenders.ToAddDocs') }}
            </sButton>
          </template>
        </EmptyBlock>
        <template v-if="files.length">
          <div
            class="text-left bold font-size--14">
            {{ $t('Tenders.TenderDocuments') }}
          </div>
          <sDivider class="mt-1"/>
          <DocumentList
            :attachments.sync="files"
            :can_delete="tenderStatusId == statuseKeys.REGISTRY && can_edit"
            :change="tenderStatusId == statuseKeys.SUBMISSION && can_edit"
            class="mt-2"
            data-qa="document-list"
            :item="tender"/>
        </template>
        <template v-if="filesForWinners.length">
          <div
            class="text-left bold font-size--14">
            {{ $t('Tenders.TenderWinnerDocuments') }}
          </div>
          <sDivider class="mt-1"/>
          <DocumentList
            :attachments.sync="filesForWinners"
            :can_delete="false"
            :change="false"
            class="mt-2"
            data-qa="document-winners-list"
            :item="tender"/>
        </template>
        <template v-if="adittionalAttachments.length">
          <div
            class="mt-3 text-left bold font-size--14">
            {{ $t('Tenders.InternalTenderDocuments') }}
          </div>
          <div
            class="text-left font-size--10">
            {{ $t('Tenders.DocumentsReflectedOnlyOrganizer') }}
          </div>
          <sDivider class="mt-1"/>
          <DocumentList
            :attachments.sync="adittionalAttachments"
            :can_delete="adittionalAttachmentOptions.canDelete"
            :change="adittionalAttachmentOptions.canEdit"
            class="mt-2"
            data-qa="adittional-document-list"
            document-type="INTERNAL"
            :item="tender"/>
        </template>
        <template v-if="publicAttachments.length">
          <div
            class="mt-3 text-left bold font-size--14">
            {{ $t('Tenders.ExternalTenderDocuments') }}
          </div>
          <div
            class="text-left font-size--10">
            {{ $t('Tenders.InfoExternalTenderDocuments') }}
          </div>
          <sDivider class="mt-1"/>
          <DocumentList
            :attachments.sync="publicAttachments"
            :can_delete="publicAttachmentOptions.canDelete"
            :change="publicAttachmentOptions.canEdit"
            class="mt-2"
            data-qa="public-document-list"
            document-type="PUBLIC_RESULTS"
            :item="tender"/>
        </template>
      </v-tab-item>
      <!-- members -->
      <MembersTab
        ref="membersTab"
        :bidding_type="bidding_type"
        :contranct-props="contranctProps"
        :get-subscribed-to-category="getSubscribedToCategory"
        :in_creating="in_creating"
        :items.sync="members"
        :load.sync="load_members"
        :member-search.sync="memberSearch"
        :return-to-step="returnToStep"
        :subscribed-to-category="subscribedToCategory"
        :subscribers-users="subscribersUsers"
        :tab="tabs[tab]"
        :tender="tender"/>
      <AuctionsTab
        v-if="useAuctionTab"
        :auctions="auctions"
        :loading="auctions_loading"
        :tender="tender"/>
      <!-- aiRequirements -->
      <v-tab-item
        data-qa="item-tab-aiRequirements">
        <AiRequirementsTab
          :requirement.sync="requirement.item"
          :tender="tender"/>
      </v-tab-item>
    </v-tabs-items>
  </v-card-text>
</template>
<script>
import BadgeAI from 'components/common/elements/BadgeAI.vue'
import SearchInput from 'components/common/inputs/SearchInput.vue'
import ImportDialog from 'components/common/dialogs/ImportDialog.vue'
import TooltipedBtn from 'components/common/elements/TooltipedBtn.vue'
import DownloadDocumentArchive from 'components/common/blocks/DownloadDocumentArchive.vue'
import EmptyBlock from 'components/common/elements/EmptyBlock.vue'
import DocumentList from 'components/common/blocks/DocumentList.vue'
import CriteriaTable from 'components/tenders/critetia/CriteriaTable.vue'
import LotsTab from 'components/tenders/tender page/tender tabs/LotsTab.vue'
import CriteriaCardVars from 'components/tenders/critetia/CriteriaCardVars.vue'
import OffersTab from 'components/tenders/tender page/tender tabs/OffersTab.vue'
import MembersTab from 'components/tenders/tender page/tender tabs/MembersTab.vue'
import AuctionsTab from 'components/tenders/tender page/tender tabs/AuctionsTab.vue'
import MembersBtns from 'components/tenders/tender page/tender tabs/MembersBtns.vue'
import ReducedPriceFormula from 'components/tenders/critetia/ReducedPriceFormula.vue'
import AiRequirementsTab from 'components/tenders/tender page/tender tabs/AiRequirementsTab.vue'
import AiRequirementsAddedEdit from 'components/tenders/tender page/tender tabs/AiRequirementsAddedEdit.vue'

import { modalMixin, measureMixin, tenderMixin, snackbarMixin } from 'mixins'
import {ParticipantsGroup} from '@/types/participants.js'
import SmarttenderApi from '@/api/smarttenderApi'
const api = new SmarttenderApi()

import AiApi from '@/api/aiApi'
const aiApi = new AiApi()

import { TenderFile } from '@/types/commonFile.js'
import { eventBus } from '@/main'

export default {
  name: 'tenderItemTabs',
  mixins: [modalMixin, measureMixin, tenderMixin, snackbarMixin],
  data() {
    return {
      contranctProps: {
        contractLoad: false,
        contractLoaded: false,
        contract: []
      },
      tab: 0,
      selected: [],
      panel: [0, 1],
      members: [],
      subscribedToCategory: [],
      files: [],
      filesForWinners: [],
      adittionalAttachments: [],
      publicAttachments: [],
      memberSearch: '',
      offersSearch: '',
      new_fles: [],
      criteryCard: null,
      contracts: null,
      currencies: [],
      load_criteryCard: false,
      load_members: false,
      auctions_loading: false,
      auctions: [],
      requirement: {
        item: null,
        editModal: false,
      }
    }
  },
  methods: {
    async getSubscribedToCategory() {
      if ( this.bidding_type?.isPublic || this.bidding_type?.showSubscribedUsers ) {
        this.load_members = true
        try {
          const subscribedToCategory =  await this.$_getSubscribed(this.tender)
          this.subscribedToCategory = subscribedToCategory.map(item => new ParticipantsGroup(item))
            .sort((a,b) => b.participationCount - a.participationCount || a.organizationTitle?.localeCompare(b.organizationTitle))
        } finally {
          this.load_members = false
        }
      }
    },
    memberCount(set) {
      const membersEmail = this.members.map(i => i.contactId || i.email.toLowerCase())
      let subscribersUsersEmail = []
      // let count = this.members?.length

      const bidderInfoEmails = this.$_.filter(this.tender?.bids, bid => {
        let members = this.members || [],
          subs = this.subscribersUsers,
          contactId = bid?.bidderInfo?.contactId

        return !this.$_.some( members, member => contactId == member?.contactId)
          && !this.$_.some( subs, member => contactId == member?.contactId)
      }).map(i => i.bidderInfo.contactId || i.bidderInfo.email.toLowerCase())

      if ( this.bidding_type?.isPublic || this.bidding_type?.showSubscribedUsers ) {
        subscribersUsersEmail = this.subscribersUsers.map(i => i.contactId || i.email.toLowerCase())
      }
      const count = []
        .concat(membersEmail)
        .concat(subscribersUsersEmail)
        .concat(bidderInfoEmails)
        .filter((v, i, a) => a.indexOf(v) === i)
        .length
      if ( set ) {
        let member_tab = this.tabs.find(tab => tab.value == 'members')
        member_tab.count = count
      }
      if (this.updateMembersCount) {
        this.updateMembersCount(count)
      }
      return count
    },
    updateOffersView() {
      this.$refs.offers_tab?.updateOffersView()
    },
    applyOfferSearch(search) {
      this.$refs.offers_tab?.applyOfferSearch(search)
    },
    createFileHandler(file) {
      return new TenderFile(this.tender.tenderId, null, null, file, file?.title, file?.size)
    },
    async addNewFiles() {
      await this.$_uploadFiles(this.new_fles).then(res => {
        this.$log(res)
        this.files.push( ...this.new_fles)
        this.new_fles = []
      })
    },
    returnToStep(step) {
      if (this.newFlou) {
        this.$emit('returnToStep', step)
        return
      }
      this.$router.push({
        name: this.$route.name,
        query: {step, id: this.tender.tenderId} ,
        params: { org: this.$route.params.org }
      })
    },
    setMembersGroups() {
      this.$nextTick(this.$refs.membersTab.setMembersGroups)
    },
    confirmExitChanginngMode_tenderItemTabs(ev) {
      eventBus.$emit('onConfirmExitChanginngMode', ev)
    },
    async parseValue(tender) {
      this.files = []
      this.filesForWinners = []
      this.updateOffersView()

      this.setTenderMeasurements()
      if ( this.bidding_type?.isFrameFirstStage && tender.tenderStatus.id == this.statuseKeys.COMPLETED ) {
        this.auctions_loading = true
        api.getBindedFrameAuctions(tender.tenderId).then(res => {
          this.auctions = res
          this.auctions_loading = false
        })
      }
      if (tender && tender?.attachments && tender?.attachments?.length) {
        tender?.attachments?.forEach(file => {
          const getFile = (file) => {
            var f = new TenderFile(tender?.tenderId, file?.id, file?.key, null, file?.title, file?.size, file.documentType)
            f.created = file?.created ? new Date(file?.created) : null
            f.dateModified = !file?.dateModified ? null : new Date(file.dateModified)
            f.isActive = file?.isActive
            f.versions = []
            return f
          }

          var f = getFile(file)
          file?.attachments?.forEach(attachment => {
            f.versions.push(getFile(attachment))
          })
          if (file.documentType === 'PUBLIC') {
            this.files.push(f)
          } else if (file.documentType === 'INTERNAL') {
            this.adittionalAttachments.push(f)
          } else if (file.documentType === 'PUBLIC_RESULTS') {
            this.publicAttachments.push(f)
          } else if (file.documentType === 'WINNER') {
            this.filesForWinners.push(f)
          }
        })
      }
      this.load_criteryCard = true
      this.criteryCard = await this.$store.dispatch('infoAboutCriterionCard', {save: !this.in_creating, id: tender.criteriaCard})
        .finally(() => this.load_criteryCard = false)

      this.getRequirement()
    },
    getRequirement() {
      if (!this.growthBook.ai_functionals || !this.tender?.tenderId) {
        return
      }
      aiApi.getRequirement(this.tender.tenderId)
        .then((response) => {
          this.requirement.item = response
        })
    }
  },
  computed: {
    isHistoryButtonEnabled() {
      if (!this.tender?.hasBids) {
        return false
      }

      // На 'Прийом пропозицій' и 'Аукціон' при настройке прячем историю
      const hideStatuses = [this.statuseKeys.SUBMISSION, this.statuseKeys.AUCTION].includes(this.tender.tenderStatusId ?? this.tender?.tenderStatus?.id)
      if (this.settings?.tenderSettings?.hideProposalsUntilRemoveTenderFromSite && hideStatuses) {
        return false
      }
      return true
    },
    /**
     * На этих статусах нельзя загружать архивом
      1: {statusCode: 1, title: "Реєстрація"}
      4: {statusCode: 4, title: "Прийом пропозицій"}
      7: {statusCode: 7, title: "Аукціон"}
     */
    disabledDocumentArchive() {
      return [this.statuseKeys.REGISTRY, this.statuseKeys.SUBMISSION, this.statuseKeys.AUCTION].includes(this.tender?.tenderStatus?.id)
    },
    isTerminalStatus() {
      return [this.statuseKeys.COMPLETED, this.statuseKeys.CANCELED, this.statuseKeys.PURCHASE_NOT_PLACED].includes(this.tender?.tenderStatus?.id)
    },
    isSubmissionOrAuctionStatus() {
      return [this.statuseKeys.SUBMISSION, this.statuseKeys.AUCTION].includes(this.tenderStatusId)
    },
    publicAttachmentOptions() {
      if (this.tenderStatusId === this.statuseKeys.BID_ANALYSIS) {
        return {
          canEdit: this.$can('set', 'publicTenderResultsAttachments'),
          canDelete: this.$can('set', 'publicTenderResultsAttachments')
        }
      }
      return {
        canEdit: (
          this.$can('change', 'tenderWinnerChangeMethodAccessibility') &&
          this.$can('set', 'publicTenderResultsAttachments') &&
          this.isTerminalStatus
        ) || this.tenderStatusId === this.statuseKeys.REGISTRY,
        canDelete: this.$can('set', 'publicTenderResultsAttachments') && this.isSubmissionOrAuctionStatus
      }
    },
    adittionalAttachmentOptions() {
      return {
        canEdit: (
          this.$can('change', 'tenderWinnerChangeMethodAccessibility') &&
          this.$can('set', 'addAdditionalAttachmentsAfterTendering') &&
          this.isTerminalStatus
        ) || this.tenderStatusId === this.statuseKeys.REGISTRY,
        canDelete: this.$can('set', 'addAdditionalAttachmentsAfterTendering') && this.isSubmissionOrAuctionStatus
      }
    },
    registration() {
      return this.in_creating || this.tenderStatusId == this.statuseKeys.REGISTRY && !this.tender?.bids?.length
    },
    biddingTypeId() {
      return this.tender?.biddingTypeId || this.tender?.biddingType?.biddingTypeId
    },
    bidding_types() {
      let bidding_types = {}
      this.$store.getters.getBiddingTypes?.forEach(i => {
        this.$set(bidding_types, this.biddingTypeId ? i?.biddingTypeId : i?.oldCode , i)
      })
      return bidding_types
    },
    bidding_type() {
      return this.bidding_types[this.biddingTypeId || this.tender?.biddingType?.code] || this.tender?.biddingType
    },
    tabs() {
      let tabs = [
        {
          name: this.$t('Tenders.Lots'),
          value: 'lots',
          count: this.tender?.lots?.length
        },
        {
          name: this.$t('Tenders.Offers'),
          value: 'offers',
          count: this.$_.reduce(this.tender?.bids, (res , bid) => res + bid.bidDetails.filter( bid => ['ACTIVE', 'DRAFT'].includes(bid.bidStatus) )?.length, 0)
        },
        {
          name: this.$t('Tenders.Criteria'),
          value: 'criteria',
          count: this.criteryCard?.criterias?.length
        },
        {
          name: this.$t('Tenders.Documents'),
          value: 'documents',
          count: this.files?.length + this.adittionalAttachments?.length + this.publicAttachments?.length + this.filesForWinners?.length
        },
      ]

      if (!this.tender.biddingType?.withoutPublication) {
        tabs.push(
          {
            name: this.$t('Tenders.InvitedParticipants'),
            value: 'members',
            count: this.memberCount()
          })
      } else if (this.updateMembersCount) {
        this.updateMembersCount(0)
      }

      let in_creating = this.registration && !this.tender?.bids?.length
      if ( in_creating || !this.$can('use', 'useCriteriaCards')) {
        tabs = tabs.filter(el => {
          return (this.$can('use', 'useCriteriaCards') || el.value != 'criteria')
                  && ( !in_creating || el.value != 'offers')
        })
      }
      if (this.useAuctionTab) {
        tabs.push(
          {
            name: this.$t('Tenders.Auctions'),
            value: 'auctions',
            count: this.auctions?.length
          })
      }
      if (this.growthBook.ai_functionals && (this.requirement?.item?.requirements?.length || this.$can('use', 'aiRequirementsFromDocument'))) {
        tabs.push(
          {
            name: this.$t('AIRequirements.Requirements'),
            value: 'aiRequirements',
            count: 0
          })
      }
      return tabs
    },
    useAuctionTab() {
      return this.bidding_type?.isFrameFirstStage && this.tenderStatusId == this.statuseKeys.COMPLETED
    },
    expand_headers() {
      return [{ text: '', value: '', width: '50px', sortable: false },
        { text: '', value: 'userConcatNameEmail' },
        { text: '', value: 'empty', width: '50px', filterable: false, sortable: false },
      ]
    },
    per_page() {
      return Math.round(document.documentElement.clientHeight / 45)
    },
    subscribersUsers() {
      return this.$_getSubscribersUseers()
    },
    tenderStatusId() {
      return this.tender.tenderStatus?.id || this.tender.tenderStatusId
    },
    can_invite() {
      return this.in_creating ? !!this.tabs[this.tab]?.count :
        this.tenderStatusId && this.can_add_file
    },
    can_add_file() {
      return this.tenderStatusId == this.statuseKeys.SUBMISSION || this.tenderStatusId == this.statuseKeys.REGISTRY || this.tenderStatusId == this.statuseKeys.AUCTION
    }
  },
  watch: {
    tenderStatusId() {
      if (!this.tabs[this.tab]) {
        return
      }
      this.updateOffersView()
      if (this.tabs[this.tab]?.value == 'members') {
        this.setMembersGroups()
      } else if (this.tabs[this.tab]?.value == 'offers') {
        this.applyOfferSearch()
      }
    },
    change_winner_mode(val) {
      if ( val ) {
        if ( this.$refs.offers_tab ) {
          this.$root.$emit('changeSnackbar', {name: 'change-winner'})
        }
        this.$refs.tabs.$el.addEventListener('click', this.confirmExitChanginngMode_tenderItemTabs, true)
      } else {
        this.$refs.tabs.$el.removeEventListener('click', this.confirmExitChanginngMode_tenderItemTabs, true)
      }
      this.updateOffersView()
      this.applyOfferSearch()
    },
    async tab(val) {
      if ( this.change_winner_mode && val != 1 ) {
        this.tab = 1
        return
      }

      let tab = this.tabs[val || 0]

      this.$_closeSnackbar()
      this.$emit('update:selected_bid', null )
      if ( tab.value == 'members') {
        this.setMembersGroups()
      } else if ( tab.value == 'offers' ) {
        this.applyOfferSearch()
      }
    },
    tender: {
      immediate: true,
      deep: true,
      async handler(newValue, oldValue) {
        if (newValue.tenderId !== oldValue?.tenderId) {
          this.parseValue(newValue)
        }
      }
    }
  },
  props: {
    tender: Object,
    in_creating: Boolean,
    updateMembersCount: Function,
    change_winner_mode: Boolean,
    can_edit: Boolean,
    newFlou: Boolean

  },
  async created() {
    if ( this.$route?.query?.tab ) {
      const indexTab = this.tabs.findIndex(i => i.value === this.$route?.query?.tab)
      if (indexTab !== -1) {
        this.tab = indexTab
      }
      this.$router.replace('')
      // return
    }
  },
  unmounted() {
    for (const key in this.files) {
      if (this.files.hasOwnProperty(key)) {
        const file = this.files[key]
        window.URL.revokeObjectURL(file.url)
      }
    }
  },
  components: {
    BadgeAI,
    LotsTab,
    OffersTab,
    EmptyBlock,
    MembersTab,
    SearchInput,
    AuctionsTab,
    MembersBtns,
    ImportDialog,
    TooltipedBtn,
    DocumentList,
    CriteriaTable,
    CriteriaCardVars,
    AiRequirementsTab,
    ReducedPriceFormula,
    AiRequirementsAddedEdit,
    DownloadDocumentArchive,
  }
}
</script>
<style lang="scss" scoped>
::v-deep .v-slide-group__wrapper {

  contain: none;
}
.underlined-btn {
  font-size: 12px;
}

.tab {
  min-width: unset !important;
  letter-spacing: 0.01em;
}

.chip {
  height: 18px;
  border-radius: 10px;
}

.offer-info {
  div {
    text-align: start;
    font-size: 13px;
  }

  .info-item-title {
    font-weight: 500;
  }

  .v-subheader {
    height: 15px;
  }
}
</style>
<style lang="scss">
#tender-tabs {
  .search-input {
    height: 30px;
    .v-input__control {
      height: 30px;
      min-height: 30px;
    }
  }

  .tender-tabs {
    .tab {
      &__badge {
        position: relative;
        bottom: 5px;
        left: 4px;
      }
    }

    .v-item-group ,.v-slide-group__content {
      height: 30px;
      .v-btn {
        height: 30px;
      }
    }
  }

  .documents-list .v-list-item {
    border-radius: 8px !important;
  }

  .tender-table {
    color: var(--text-color) !important;

    &.v-data-table tr {
      background-color: var(--v-main__light_green-base) !important;

      &.v-data-table__expanded {

        &,
        tr,
        td {
          background-color: var(--v-secondary__green_chosen-base) !important;
        }

        background-color: var(--v-secondary__green_chosen-base) !important;

        .additional-description {
          padding: 16px 0 24px !important;
        }

        &.v-data-table__expanded__row {

          td:first-child,
          td:last-child {
            border: none !important;
          }
        }
      }

      &:hover:not(.v-data-table__expanded) {
        background-color: var(--v-main__light_green-base) !important;
      }

      td.text-start,
      th {
        // padding: 0 !important;
        white-space: nowrap;
      }
    }

    /*&.lots:not(.in_creating):not(.is-default-cursor),
    &.offers:not(.is-default-cursor) {
      td {
        cursor: pointer;
      }
    }*/

    &.lots.in_creating {
      td {
        .description-comment {
          cursor: pointer;
        }

        cursor: default;
      }
    }

    &.criteria {
      &.v-data-table tbody td {
        vertical-align: text-top;
        cursor: default;
      }

      &.v-data-table thead th,
      &.v-data-table tbody td {
        padding: 10px 5px 10px 24px !important;
      }
    }

    &.offers,
    &.lots,
    &.members-table {

      &.v-data-table thead th,
      &.v-data-table tbody td {

        &:first-child,
        &:last-child {
          padding: 10px !important;
        }

        padding: 15px 5px !important;
      }
    }

    .comments {
      max-height: 25px;
    }
  }

  .members-table {
    // cursor: default;
    &.v-data-table {
      thead.v-data-table-header tr th {
        border-bottom: thin solid var(--v-main__green-base) !important;
      }
    }

    .v-data-table__progress {
      display: none;
    }
  }

  .documents .attachments-list .v-list-item {
    border-radius: 8px;
  }
}
</style>
